import { createContext, useContext, useCallback, useEffect, useRef, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { isVoiceInputSupported } from "@/lib/speechUtils";

interface VoiceFieldRegistration {
  id: string;
  onTranscript: (text: string) => void;
}

interface VoiceInputContextValue {
  isAnyRecording: boolean;
  setIsAnyRecording: (v: boolean) => void;
  registerField: (reg: VoiceFieldRegistration) => void;
  unregisterField: (id: string) => void;
  activeFieldId: string | null;
  setActiveField: (id: string | null) => void;
}

const VoiceInputContext = createContext<VoiceInputContextValue | null>(null);

export function useVoiceInputContext() {
  return useContext(VoiceInputContext);
}

export default function VoiceInputProvider({ children }: { children: React.ReactNode }) {
  const [isAnyRecording, setIsAnyRecording] = useState(false);
  const [activeFieldId, setActiveField] = useState<string | null>(null);
  const fieldsRef = useRef<Map<string, VoiceFieldRegistration>>(new Map());
  const { toast } = useToast();

  const registerField = useCallback((reg: VoiceFieldRegistration) => {
    fieldsRef.current.set(reg.id, reg);
  }, []);

  const unregisterField = useCallback((id: string) => {
    fieldsRef.current.delete(id);
  }, []);

  // Global keyboard shortcut: Ctrl+Shift+M / Cmd+Shift+M
  useEffect(() => {
    if (!isVoiceInputSupported()) return;

    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === "m") {
        e.preventDefault();

        // If there's no active/focused field, show a hint
        if (!activeFieldId && fieldsRef.current.size === 0) {
          toast({
            title: "Voice Input",
            description: "Click on a text field first, then press Ctrl+Shift+M",
          });
          return;
        }

        // Dispatch a custom event that VoiceInputButton instances can listen to
        window.dispatchEvent(
          new CustomEvent("voice-input-shortcut", {
            detail: { fieldId: activeFieldId },
          })
        );
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [activeFieldId, toast]);

  return (
    <VoiceInputContext.Provider
      value={{
        isAnyRecording,
        setIsAnyRecording,
        registerField,
        unregisterField,
        activeFieldId,
        setActiveField,
      }}
    >
      {children}
    </VoiceInputContext.Provider>
  );
}
